import { ReactNode } from 'react';
import { InjectedIntlProps, injectIntl, intlShape } from 'react-intl';
import { APIPropTypes, PropTypes } from 'typed/imports';
import { ComponentEventHandler, ComponentEventHandler1, ConnectedProps, PureComponent, ReactEventHandler, ReactEventHandler1, connect } from 'typed/components/utils';
import { AdministrationUserType, DBBoolean, ProductPackage, RequestStatus, SortOrder, SystemUserType, UserRoleBillingUserType, entities } from 'typed/api';
import * as actions from 'typed/actions';
import * as untypedActions from 'actions';
import { Checkbox, FormattedResource } from 'typed/components/common';
import { DropPanel, MarkdownBlock, Popup, TextInput, TimeStamp } from 'components/common';
import { ComponentLoader } from 'components/routing';
import { Scale, deepAssign } from 'typed/utils';
import styles from '!!less-vars-loader!./UserSelectionList.less';
import './UserSelectionList.less';

export enum TableColumn {
    SELECTION = 1,
    USER = 2,
    EMAIL = 3,
    ROLE = 4,
    TEAM = 5,
    POSITION = 6,
    LAST_LOGIN = 7
}

export interface UserListFilter {
    CompanyFunctionIDs?: number[];
    CompanyVirtualTeamIDs?: number[];
}

const
    ALL_COLUMNS: TableColumn[] = Object.values(TableColumn).filter(v => +v).map(v => +v),
    COLUMN_SCALE = new Scale({
        [TableColumn.SELECTION]: '',
        [TableColumn.USER]: { id: 'common.name' },
        [TableColumn.EMAIL]: { id: 'common.email' },
        [TableColumn.ROLE]: { id: 'users.system_role' },
        [TableColumn.TEAM]: { id: 'users.team' },
        [TableColumn.POSITION]: { id: 'users.position' },
        [TableColumn.LAST_LOGIN]: { id: 'users.last_login' }
    }),

    TABLE_HORIZONTAL_PADDING = parseInt(styles.tableHorizontalPadding),
    TABLE_CELL_HORIZONTAL_PADDING = parseInt(styles.tableCellHorizontalPadding),
    CHECKBOX_WIDTH = parseInt(styles.checkboxWidth),
    SELECTION_CELL_WIDTH = TABLE_HORIZONTAL_PADDING + CHECKBOX_WIDTH + TABLE_CELL_HORIZONTAL_PADDING,

    USER_ADMIN = Symbol('USER_ADMIN'),
    STRATEGY_FACILITATOR = Symbol('STRATEGY_FACILITATOR'),
    EXTERNAL_USER = Symbol('EXTERNAL_USER');

type RoleIdentifier = typeof USER_ADMIN | typeof STRATEGY_FACILITATOR | typeof EXTERNAL_USER | number;

interface UserRow {
    user: entities.UserLocal;
    access: entities.UserCompanyAccess;
    roles: { id: RoleIdentifier; name: string; }[];
    team: string;
    virtualTeamID: number | null;
    virtualTeamName: string;
    loginDate: Date | null;
}

interface StoreProps {
    users: entities.NetworkArray<entities.UserLocal>;
    userCompanyAccesses: entities.NetworkArray<entities.UserCompanyAccess>;
    userReport: entities.NetworkArray<entities.UserListDataReport>;
    systemUserTypes: entities.NetworkArray<entities.SystemUserType>;
    userRoleBillingUserTypes: entities.NetworkArray<entities.UserRoleBillingUserType>;
    companyFunctions: entities.NetworkArray<entities.CompanyFunction>;
    companyVirtualTeams: entities.NetworkArray<entities.CompanyVirtualTeam>;
    companyVirtualTeamMembers: entities.NetworkArray<entities.CompanyVirtualTeamMember>;
}

interface OwnProps extends InjectedIntlProps {
    fixedHeader?: boolean;
    columns?: TableColumn[];
    value?: number[];
    isMultiSelect?: boolean;
    onlyNormalUsers?: boolean;
    onlyManagedUsers?: boolean;
    hiddenUserIDs?: number[];
    showUserCount?: boolean;
    defaultFilter?: UserListFilter;
    companyVirtualTeamIDs?: number[];
    disabled?: boolean;
    onChange?: ComponentEventHandler1<number[]>;
    onUserEditingStatusChange?: ComponentEventHandler1<boolean>;
}

interface Props extends StoreProps, OwnProps, ConnectedProps {}

interface State {
    headerWidth: number;

    sorting: { column: TableColumn; order: SortOrder; }[];
    userCount: number;
    users: UserRow[];

    userFilter: string;
    emailFilter: string;
    roleFilter: RoleIdentifier[];
    teamFilter: number[];
    virtualTeamFilter: number[];
    positionFilter: string[];

    functionsFilterGroupExpanded: boolean;
    virtualTeamsFilterGroupExpanded: boolean;
    expandedFunctionIDs: number[];

    filterInitialized: boolean;

    editedUser: entities.UserLocal | null;
}

class UserSelectionList extends PureComponent<Props, State> {
    static readonly propTypes = {
        intl: intlShape.isRequired,

        fixedHeader: PropTypes.bool,
        columns: PropTypes.arrayOf(PropTypes.oneOf(ALL_COLUMNS)),
        value: PropTypes.arrayOf(PropTypes.number),
        isMultiSelect: PropTypes.bool,
        onlyNormalUsers: PropTypes.bool,
        onlyManagedUsers: PropTypes.bool,
        hiddenUserIDs: PropTypes.arrayOf(PropTypes.number),
        showUserCount: PropTypes.bool,
        defaultFilter: PropTypes.shape({
            CompanyFunctionIDs: PropTypes.arrayOf(PropTypes.number),
            CompanyVirtualTeamIDs: PropTypes.arrayOf(PropTypes.number)
        }),
        disabled: PropTypes.bool,
        onChange: PropTypes.func,
        onUserEditingStatusChange: PropTypes.func,

        users: APIPropTypes.networkArrayOf(APIPropTypes.UserLocal).isRequired,
        userCompanyAccesses: APIPropTypes.networkArrayOf(APIPropTypes.UserCompanyAccess).isRequired,
        systemUserTypes: APIPropTypes.networkArrayOf(APIPropTypes.SystemUserType).isRequired,
        userRoleBillingUserTypes: APIPropTypes.networkArrayOf(APIPropTypes.UserRoleBillingUserType).isRequired,
        companyFunctions: APIPropTypes.networkArrayOf(APIPropTypes.CompanyFunction).isRequired,
        companyVirtualTeams: APIPropTypes.networkArrayOf(APIPropTypes.CompanyVirtualTeam).isRequired,
        companyVirtualTeamMembers: APIPropTypes.networkArrayOf(APIPropTypes.CompanyVirtualTeamMember).isRequired
    };

    static readonly defaultProps = {
        fixedHeader: false,
        columns: ALL_COLUMNS,
        value: [],
        isMultiSelect: true,
        onlyNormalUsers: false,
        onlyManagedUsers: true,
        hiddenUserIDs: [],
        showUserCount: false,
        disabled: false
    };

    state: State = {
        headerWidth: 0,

        sorting: [],
        userCount: 0,
        users: [],

        userFilter: '',
        emailFilter: '',
        roleFilter: [],
        teamFilter: [],
        virtualTeamFilter: [],
        positionFilter: [],

        functionsFilterGroupExpanded: false,
        virtualTeamsFilterGroupExpanded: false,
        expandedFunctionIDs: [],

        filterInitialized: false,

        editedUser: null
    };

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps: Props) {
        if (nextProps.users.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(untypedActions.loadAllUsers());
        }

        if (nextProps.userCompanyAccesses.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(untypedActions.loadAllUserCompanyAccesses());
        }

        if (nextProps.userReport.Custom.status === RequestStatus.UNSYNCED && nextProps.columns!.includes(TableColumn.LAST_LOGIN)) {
            nextProps.dispatch(untypedActions.loadUserDataReport());
        }

        if (nextProps.systemUserTypes.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(actions.loadAllSystemUserTypes());
        }

        if (nextProps.userRoleBillingUserTypes.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(actions.loadAllUserRoleBillingUserTypes());
        }

        if (nextProps.companyFunctions.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(actions.companyFunctions.loadAll());
        }

        if (nextProps.companyVirtualTeams.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(actions.companyVirtualTeams.loadAll());
        }

        if (nextProps.companyVirtualTeamMembers.Custom.status === RequestStatus.UNSYNCED) {
            nextProps.dispatch(actions.companyVirtualTeamMembers.loadAll());
        }


        const
            { session } = nextProps,
            isUserBillingCompany = session.UseUserRoleBasedBilling,
            companyUsers = nextProps.users.Data
                .filter(({ ID, AdministrationUserTypeID, SystemUserTypeID }) => {
                    const access = nextProps.userCompanyAccesses.Data.find(({ UserID }) => UserID === ID);

                    if (!access || access.SystemUserTypeID === SystemUserType.WORKSHOP_PARTICIPATOR || SystemUserTypeID === SystemUserType.WORKSHOP_PARTICIPATOR) {
                        return false;
                    }

                    return !AdministrationUserTypeID || session.Is4SCompany && AdministrationUserTypeID === AdministrationUserType.ADMINISTRATOR;
                }),
            visibleUsers = companyUsers.filter(({ ID, SystemUserTypeID, DirectManagerID }) =>
                (
                    !nextProps.onlyManagedUsers || !isUserBillingCompany ||
                    session.UserRoleBillingUserTypeID === UserRoleBillingUserType.FACILITATOR ||
                    session.UserRoleBillingUserTypeID === UserRoleBillingUserType.MANAGER && (ID === session.UserID || DirectManagerID === session.UserID)
                ) &&
                (!nextProps.onlyNormalUsers || SystemUserTypeID === SystemUserType.NORMAL_USER) &&
                !(nextProps.hiddenUserIDs || []).includes(ID)
            ),
            sortedUsers = visibleUsers
                .innerJoin(nextProps.userCompanyAccesses.Data, ({ ID }) => ID, ({ UserID }) => UserID, (user, access) => {
                    const roles: UserRow['roles'] = [];

                    if (isUserBillingCompany) {
                        if (access.UserRoleBillingUserTypeID) {
                            roles.push({
                                id: access.UserRoleBillingUserTypeID as unknown as RoleIdentifier,
                                name: nextProps.userRoleBillingUserTypes.Data.getByID(access.UserRoleBillingUserTypeID)?.Description || ''
                            });
                        }
                    }
                    else {
                        roles.push({
                            id: access.SystemUserTypeID as unknown as RoleIdentifier,
                            name: nextProps.systemUserTypes.Data.getByID(access.SystemUserTypeID)?.Description || ''
                        });

                        if (access.IsUserAdministrator === DBBoolean.TRUE) {
                            roles.push({ id: USER_ADMIN as unknown as RoleIdentifier, name: nextProps.intl.formatMessage({ id: 'users.user_admin' }) });
                        }

                        if (access.IsStrategyFacilitator) {
                            roles.push({ id: STRATEGY_FACILITATOR as unknown as RoleIdentifier, name: nextProps.intl.formatMessage({ id: 'users.strategy_facilitator' }) });
                        }
                    }

                    if (access.IsExternalUser) {
                        roles.push({ id: EXTERNAL_USER as unknown as RoleIdentifier, name: nextProps.intl.formatMessage({ id: 'users.external_user' }) });
                    }

                    const virtualTeamMember = nextProps.companyVirtualTeamMembers.Data.find(({ UserID }) => UserID === user.ID);

                    return {
                        user,
                        access,
                        roles,
                        team: nextProps.companyFunctions.Data.getByID(user.CompanyFunctionID)?.Name || '',
                        virtualTeamID: virtualTeamMember?.TeamID || null,
                        virtualTeamName: virtualTeamMember ? nextProps.companyVirtualTeams.Data.getByID(virtualTeamMember.TeamID)?.Name || '' : '',
                        loginDate: nextProps.userReport.Data.getByID(user.ID)?.LastLoginDate || null
                    };
                })
                .sort(this._compareUsers.bind(this));

        this.setState({
            userCount: companyUsers.length,
            users: sortedUsers
        });

        if (!this.state.filterInitialized) {
            const
                selectedFunctionIDs = nextProps.columns!.includes(TableColumn.TEAM) && nextProps.defaultFilter?.CompanyFunctionIDs || [],
                selectedVirtualTeamIDs = nextProps.columns!.includes(TableColumn.TEAM) && nextProps.defaultFilter?.CompanyVirtualTeamIDs || [],

                expandedFunctionIDs = selectedFunctionIDs.length ?
                    nextProps.companyFunctions.Data
                        .map(({ FatherID }) => FatherID)
                        .unique()
                        .filter(ID => !!ID)
                        .map(ID => ID as number) :
                    [];

            let roleFilter: RoleIdentifier[] = [];

            if (nextProps.session.ProductPackageID !== ProductPackage.ADVISORY360 &&
                nextProps.session.ProductPackageID !== ProductPackage.SMART_STRATEGY) {
                roleFilter = nextProps.session.UseUserRoleBasedBilling ?
                    Object.values(UserRoleBillingUserType).map(v => +v as unknown as RoleIdentifier).unique().filter(v => v) :
                    Object
                        .values(SystemUserType)
                        .map(v => +v as unknown as RoleIdentifier)
                        .unique()
                        .filter(v => v && v !== SystemUserType.WORKSHOP_PARTICIPATOR)
                        .concat(USER_ADMIN, STRATEGY_FACILITATOR);
            }

            this.setState({
                filterInitialized: true,
                roleFilter,
                teamFilter: selectedFunctionIDs,
                virtualTeamFilter: selectedVirtualTeamIDs,
                functionsFilterGroupExpanded: expandedFunctionIDs.length > 0,
                virtualTeamsFilterGroupExpanded: selectedVirtualTeamIDs.length > 0,
                expandedFunctionIDs
            });
        }
    }

    componentDidMount() {
        this.componentDidUpdate(this.props, this.state);
    }

    componentDidUpdate(_prevProps: Props, _prevState: State) {
        if (this.props.fixedHeader) {
            if (this._headerWidthTimeout) {
                clearTimeout(this._headerWidthTimeout);
                this._headerWidthTimeout = null;
            }

            this._headerWidthTimeout = setTimeout(this._adjustHeaderWidth.bind(this), 200);
        }
    }

    componentWillUnmount() {
        if (this._headerWidthTimeout) {
            clearTimeout(this._headerWidthTimeout);
            this._headerWidthTimeout = null;
        }
    }

    private _headerWidthTimeout: ReturnType<typeof setTimeout> | null = null;


    private _compareUsers(userA: UserRow, userB: UserRow, sorting = this.state.sorting) {
        const rules = sorting.filter(({ order }) => order !== SortOrder.UNSPECIFIED).or([{ column: TableColumn.USER, order: SortOrder.ASCENDING }]);
        let order = 0;

        while (order === 0 && rules.length) {
            const
                rule = rules.shift()!,
                reverser = rule.order === SortOrder.DESCENDING ? -1 : 1;

            switch (rule.column) {
                case TableColumn.USER:
                    const
                        userAName = `${userA.user.FirstName} ${userA.user.LastName}`.toLowerCase(),
                        userBName = `${userB.user.FirstName} ${userB.user.LastName}`.toLowerCase();

                    order = userAName.localeCompare(userBName) * reverser;
                    break;

                case TableColumn.EMAIL:
                    order = userA.user.Email.localeCompare(userB.user.Email) * reverser;
                    break;

                case TableColumn.ROLE:
                    const
                        userARoleIndex = this.props.session.UseUserRoleBasedBilling ?
                            this.props.userRoleBillingUserTypes.Data.getIndexByID(userA.access.UserRoleBillingUserTypeID) :
                            this.props.systemUserTypes.Data.getIndexByID(userA.user.SystemUserTypeID),
                        userBRoleIndex = this.props.session.UseUserRoleBasedBilling ?
                            this.props.userRoleBillingUserTypes.Data.getIndexByID(userB.access.UserRoleBillingUserTypeID) :
                            this.props.systemUserTypes.Data.getIndexByID(userB.user.SystemUserTypeID);

                    if (userARoleIndex < 0 && userBRoleIndex >= 0) {
                        order = 1;
                    }
                    else if (userBRoleIndex < 0 && userARoleIndex >= 0) {
                        order = -1;
                    }
                    else if (userARoleIndex >= 0 && userBRoleIndex >= 0) {
                        order = (userARoleIndex - userBRoleIndex) * reverser;
                    }

                    if (order === 0) {
                        const
                            userARoles = userA.roles.filter(({ id }) => typeof id !== 'number').map(({ name }) => name).join().toLowerCase(),
                            userBRoles = userB.roles.filter(({ id }) => typeof id !== 'number').map(({ name }) => name).join().toLowerCase();

                        // The reversal happens only if both of the compared values have been set
                        order = +!!userBRoles - +!!userARoles || userARoles.localeCompare(userBRoles) * reverser;
                    }

                    break;

                case TableColumn.TEAM:
                    const
                        teamA = [userA.team, userA.virtualTeamName].filter(name => !!name).join(', '),
                        teamB = [userB.team, userB.virtualTeamName].filter(name => !!name).join(', ');

                    order = +!!teamB - +!!teamA || teamA.toLowerCase().localeCompare(teamB.toLowerCase()) * reverser;
                    break;

                case TableColumn.POSITION:
                    order = +!!userB.user.Title - +!!userA.user.Title || userA.user.Title.toLowerCase().localeCompare(userB.user.Title.toLowerCase()) * reverser;
                    break;

                case TableColumn.LAST_LOGIN:
                    order = (+userB.loginDate! - +userA.loginDate!) * reverser;
                    break;

                default:
                    break;
            }
        }

        return order;
    }

    private _adjustHeaderWidth() {
        const users = this.refs.users as HTMLElement | undefined;

        if (users) {
            const { width } = users.getBoundingClientRect();
            if (this.state.headerWidth !== width) { this.setState({ headerWidth: width }); }
        }
    }


    private _onSortUsersClick: ReactEventHandler1<TableColumn> = (column) => {
        const
            sorting = this.state.sorting.copy(),
            sortIndex = sorting.findIndex(sortRule => sortRule.column === column);

        let columnSort = sortIndex >= 0 ? sorting.splice(sortIndex, 1).first() : { column, order: SortOrder.UNSPECIFIED };

        switch (columnSort.order) {
            case SortOrder.UNSPECIFIED:
                columnSort = deepAssign(columnSort, { order: SortOrder.ASCENDING });
                break;

            case SortOrder.ASCENDING:
                columnSort = deepAssign(columnSort, { order: SortOrder.DESCENDING });
                break;

            case SortOrder.DESCENDING:
                columnSort = deepAssign(columnSort, { order: SortOrder.UNSPECIFIED });
                break;

            default:
                break;
        }

        sorting.unshift(columnSort);

        this.setState({
            sorting,
            users: this.state.users.copy().sort((a, b) => this._compareUsers(a, b, sorting))
        });
    };


    private _onUserMassSelectionChange = (value: number[], unselect: boolean) => {
        if (this.props.onChange && !this.props.disabled) {
            let nextValue = (this.props.value || []).copy();

            if (!unselect) {
                nextValue = nextValue.concat(value).unique();
            }
            else {
                nextValue = nextValue.filter(userID => !value.includes(userID));
            }

            this.props.onChange(this, nextValue);
        }
    };

    private _onUserFilterChange: ComponentEventHandler1<string> = (_sender, value) => {
        this.setState({ userFilter: value.trim() });
    };

    private _onEmailFilterChange: ComponentEventHandler1<string> = (_sender, value) => {
        this.setState({ emailFilter: value.trim() });
    };

    private _onClearFilterClick: ReactEventHandler1<TableColumn> = (column, event) => {
        (event as unknown as MouseEvent).stopPropagation();
        (event as unknown as MouseEvent).preventDefault();

        switch (column) {
            case TableColumn.ROLE:
                this.setState({ roleFilter: [] });
                break;

            case TableColumn.TEAM:
                this.setState({
                    teamFilter: [],
                    virtualTeamFilter: []
                });
                break;

            case TableColumn.POSITION:
                this.setState({ positionFilter: [] });
                break;

            default:
                break;
        }
    };

    private _onToggleRoleFilterClick: ReactEventHandler1<RoleIdentifier> = (value) => {
        const sortedIdentifiers: RoleIdentifier[] = [];

        if (this.props.session.UseUserRoleBasedBilling) {
            sortedIdentifiers.push(...this.props.userRoleBillingUserTypes.Data.map(({ ID }) => ID));
        }
        else {
            sortedIdentifiers.push(USER_ADMIN, STRATEGY_FACILITATOR, ...this.props.systemUserTypes.Data.map(({ ID }) => ID));
        }

        sortedIdentifiers.push(EXTERNAL_USER);

        this.setState({
            roleFilter: this.state.roleFilter.copy().toggle(value).sort((a, b) => {
                let
                    aIndex = sortedIdentifiers.findIndex(identifier => identifier === a),
                    bIndex = sortedIdentifiers.findIndex(identifier => identifier === b);

                if (aIndex < 0) { aIndex = Infinity; }
                if (bIndex < 0) { bIndex = Infinity; }

                return aIndex - bIndex;
            })
        });
    };

    private _onToggleFunctionsFilterGroupClick: ReactEventHandler = (event) => {
        event.stopPropagation();
        this.setState({ functionsFilterGroupExpanded: !this.state.functionsFilterGroupExpanded });
    };

    private _onToggleFunctionsFilterParentFunctionClick: ReactEventHandler1<number> = (teamID: number, event) => {
        event.stopPropagation();
        event.preventDefault();

        const expandedFunctionIDs = this.state.expandedFunctionIDs.copy();
        this.setState({ expandedFunctionIDs: expandedFunctionIDs.toggle(teamID) });
    };

    private _onToggleVirtualTeamsFilterGroupClick: ReactEventHandler = (event) => {
        event.stopPropagation();
        this.setState({ virtualTeamsFilterGroupExpanded: !this.state.virtualTeamsFilterGroupExpanded });
    };

    private _onToggleTeamFilterClick: ReactEventHandler1<number> = (value) => {
        this.setState({ teamFilter: this.state.teamFilter.copy().toggle(value).sortBy(teamID => this.props.companyFunctions.Data.getByID(teamID)?.Name || '') });
    };

    private _onToggleVirtualTeamFilterClick: ReactEventHandler1<number> = (value) => {
        this.setState({ virtualTeamFilter: this.state.virtualTeamFilter.copy().toggle(value).sortBy(teamID => this.props.companyVirtualTeams.Data.getByID(teamID)?.Name || '') });
    };

    private _onTogglePositionFilterClick: ReactEventHandler1<string> = (value) => {
        this.setState({ positionFilter: this.state.positionFilter.copy().toggle(value).sortBy(value => value.toLowerCase()) });
    };


    private _onUserSelectionChange = (userID: number, _sender: unknown, selected: boolean) => {
        let value = (this.props.value || []).copy();

        if (selected) {
            if (this.props.isMultiSelect) {
                value.push(userID);
            }
            else {
                value = [userID];
            }
        }
        else {
            value = value.filter(user => user !== userID);
        }

        if (this.props.onChange && !this.props.disabled) {
            this.props.onChange(this, value.unique());
        }
    };


    private _onEditUserClick: ReactEventHandler1<number> = (userID) => {
        const user = this.props.users.Data.getByID(userID) || null;
        this.setState({ editedUser: user });

        if (this.props.onUserEditingStatusChange && user) {
            this.props.onUserEditingStatusChange(this, true);
        }
    };

    private _onEditUserCancel: ComponentEventHandler = () => {
        this.setState({ editedUser: null });

        if (this.props.onUserEditingStatusChange) {
            this.props.onUserEditingStatusChange(this, false);
        }
    };


    private _renderHeader(columns: TableColumn[], users: UserRow[]) {
        const
            { disabled, companyFunctions, companyVirtualTeams } = this.props,
            { headerWidth, userFilter, emailFilter, teamFilter, virtualTeamFilter, functionsFilterGroupExpanded, virtualTeamsFilterGroupExpanded } = this.state,

            value = this.props.value!,
            areAllUsersSelected = !!users.length && users.every(({ user: { ID } }) => value.includes(ID));

        let columnWidth = {
            width: columns.includes(TableColumn.SELECTION) ?
                `calc((100% - ${SELECTION_CELL_WIDTH}px) / ${Math.max(1, columns.length - 1)})` :
                `${100 / Math.max(1, columns.length)}%`
        };

        if (this.props.fixedHeader && headerWidth) {
            columnWidth = {
                width: columns.includes(TableColumn.SELECTION) ?
                    `calc((${headerWidth}px - ${SELECTION_CELL_WIDTH}px) / ${Math.max(1, columns.length - 1)})` :
                    `${headerWidth / Math.max(1, columns.length)}px`
            };
        }

        return (
            <div className={`header${this.props.fixedHeader ? ' fixed' : ''}`}>
                <div className="labels">
                    {COLUMN_SCALE
                        .filter(({ point }) => columns.includes(point))
                        .map(({ point, result }) => {
                            if (point === TableColumn.SELECTION) { return <div key={point} className="column selection" />; }
                            const
                                columnSort = this.state.sorting.find(({ column }) => column === point),
                                order = columnSort ? columnSort.order : SortOrder.UNSPECIFIED;

                            return (
                                <div key={point} className="column" style={columnWidth}>
                                    <button className="inline" onClick={this._onSortUsersClick.bind(this, point)}>
                                        <div className="name"><FormattedResource resource={result} /></div>
                                        <div className="sorting">
                                            <span className={`fa fa-caret-up${order === SortOrder.DESCENDING ? ' active' : ''}`} />
                                            <span className={`fa fa-caret-down${order === SortOrder.ASCENDING ? ' active' : ''}`} />
                                        </div>
                                    </button>
                                </div>
                            );
                        })}
                </div>

                <div className="filters">
                    {
                        columns.includes(TableColumn.SELECTION) ?
                            <div className="column selection">
                                {
                                    this.props.isMultiSelect ?
                                        <Checkbox
                                            value={areAllUsersSelected}
                                            disabled={disabled}
                                            onChange={this._onUserMassSelectionChange.bind(this, users.map(({ user: { ID } }) => ID), areAllUsersSelected)}
                                        /> :
                                        null
                                }
                            </div> :
                            null
                    }

                    {
                        columns.includes(TableColumn.USER) ?
                            <div className="column" style={columnWidth}>
                                <TextInput value={userFilter} placeholder={{ id: 'common.search' }} icon="search" onChange={this._onUserFilterChange} />
                            </div> :
                            null
                    }

                    {
                        columns.includes(TableColumn.EMAIL) ?
                            <div className="column" style={columnWidth}>
                                <TextInput value={emailFilter} placeholder={{ id: 'common.search' }} icon="search" onChange={this._onEmailFilterChange} />
                            </div> :
                            null
                    }

                    {
                        columns.includes(TableColumn.ROLE) ?
                            <div className="column" style={columnWidth}>{this._renderRoleFilter()}</div> :
                            null
                    }

                    {
                        columns.includes(TableColumn.TEAM) ?
                            <div className="column" style={columnWidth}>
                                <DropPanel
                                    className={`user-selection-list-filter${!teamFilter.length && !virtualTeamFilter.length ? ' default' : ''}`}
                                    title={
                                        [
                                            ...teamFilter
                                                .innerJoin(this.props.companyFunctions.Data, teamID => teamID, ({ ID }) => ID, (_teamID, team) => team.Name),
                                            ...virtualTeamFilter
                                                .innerJoin(this.props.companyVirtualTeams.Data, teamID => teamID, ({ ID }) => ID, (_teamID, team) => team.Name)
                                        ].join(', ') || { id: 'users.team' }
                                    }
                                    // eslint-disable-next-line react/jsx-handler-names
                                    icon={
                                        teamFilter.length ?
                                            <button className="inline fa fa-times" onClick={this._onClearFilterClick.bind(this, TableColumn.TEAM)} /> :
                                            DropPanel.AUTO_ICON
                                    }
                                >
                                    <button key="teams" className="inline group" disabled={!companyFunctions.Data.length} onClick={this._onToggleFunctionsFilterGroupClick}>
                                        <span className={`fa fa-${functionsFilterGroupExpanded || !companyFunctions.Data.length ? 'minus' : 'plus'}-square-o`} />
                                        <FormattedResource resource={{ id: 'organization_structure.functions' }} />
                                    </button>
                                    {this._renderTeamFilterOptions(null, 2)}

                                    <button key="virtual-teams" className="inline group" disabled={!companyVirtualTeams.Data.length} onClick={this._onToggleVirtualTeamsFilterGroupClick}>
                                        <span className={`fa fa-${virtualTeamsFilterGroupExpanded || !companyVirtualTeams.Data.length ? 'minus' : 'plus'}-square-o`} />
                                        <FormattedResource resource={{ id: 'organization_structure.virtual_teams' }} />
                                    </button>
                                    {this._renderVirtualTeamFilterOptions()}
                                </DropPanel>
                            </div> :
                            null
                    }

                    {
                        columns.includes(TableColumn.POSITION) ?
                            <div className="column" style={columnWidth}>{this._renderPositionFilter()}</div> :
                            null
                    }

                    {columns.includes(TableColumn.LAST_LOGIN) ? <div className="column" style={columnWidth} /> : null}
                </div>
            </div>
        );
    }

    private _renderRoleFilter() {
        const
            { roleFilter } = this.state,
            title = roleFilter
                .map((id) => {
                    if (id === USER_ADMIN) { return this.props.intl.formatMessage({ id: 'users.user_admin' }); }
                    if (id === STRATEGY_FACILITATOR) { return this.props.intl.formatMessage({ id: 'users.strategy_facilitator' }); }
                    if (id === EXTERNAL_USER) { return this.props.intl.formatMessage({ id: 'users.external_user' }); }

                    return this.props.session.UseUserRoleBasedBilling ?
                        this.props.userRoleBillingUserTypes.Data.getByID(id)?.Description || '' :
                        this.props.systemUserTypes.Data.getByID(id)?.Description || '';
                })
                .join(', ');

        return (
            <DropPanel
                className={`user-selection-list-filter${!roleFilter.length ? ' default' : ''}`}
                title={title || { id: 'users.system_role' }}
                // eslint-disable-next-line react/jsx-handler-names
                icon={roleFilter.length ? <button className="inline fa fa-times" onClick={this._onClearFilterClick.bind(this, TableColumn.ROLE)} /> : DropPanel.AUTO_ICON}
            >
                {
                    this.props.session.UseUserRoleBasedBilling ?
                        this.props.userRoleBillingUserTypes.Data.map(({ ID, Description }, _, __, checked = roleFilter.includes(ID)) =>
                            <button key={ID} className={`inline${checked ? ' checked' : ''}`} onClick={this._onToggleRoleFilterClick.bind(this, ID)}>
                                <span className="formatted-resource">{Description}</span>
                                {checked ? <span className="fa fa-check" /> : null}
                            </button>
                        ) :
                        [
                            <button
                                key="user-admin"
                                className={`inline${roleFilter.includes(USER_ADMIN) ? ' checked' : ''}`}
                                onClick={this._onToggleRoleFilterClick.bind(this, USER_ADMIN)}
                            >
                                <FormattedResource resource={{ id: 'users.user_admin' }} />
                                {roleFilter.includes(USER_ADMIN) ? <span className="fa fa-check" /> : null}
                            </button>,
                            <button
                                key="strategy-facilitator"
                                className={`inline${roleFilter.includes(STRATEGY_FACILITATOR) ? ' checked' : ''}`}
                                onClick={this._onToggleRoleFilterClick.bind(this, STRATEGY_FACILITATOR)}
                            >
                                <FormattedResource resource={{ id: 'users.strategy_facilitator' }} />
                                {roleFilter.includes(STRATEGY_FACILITATOR) ? <span className="fa fa-check" /> : null}
                            </button>,
                            ...this.props.systemUserTypes.Data
                                .filter(({ ID }) => !this.props.onlyNormalUsers && ID !== SystemUserType.WORKSHOP_PARTICIPATOR || ID === SystemUserType.NORMAL_USER)
                                .map(({ ID, Description }, _, __, checked = roleFilter.includes(ID)) =>
                                    <button key={ID} className={`inline${checked ? ' checked' : ''}`} onClick={this._onToggleRoleFilterClick.bind(this, ID)}>
                                        <span className="formatted-resource">{Description}</span>
                                        {checked ? <span className="fa fa-check" /> : null}
                                    </button>
                                )
                        ]
                }

                {
                    this.props.session.ProductPackageID !== ProductPackage.ADVISORY360 &&
                    this.props.session.ProductPackageID !== ProductPackage.SMART_STRATEGY ?
                        <button
                            className={`inline${roleFilter.includes(EXTERNAL_USER) ? ' checked' : ''}`}
                            onClick={this._onToggleRoleFilterClick.bind(this, EXTERNAL_USER)}
                        >
                            <FormattedResource resource={{ id: 'users.external_user' }} />
                            {roleFilter.includes(EXTERNAL_USER) ? <span className="fa fa-check" /> : null}
                        </button> :
                        null
                }
            </DropPanel>
        );
    }

    private _renderTeamFilterOptions(fatherID: number | null = null, depth = 1): ReactNode[] {
        if (!this.state.functionsFilterGroupExpanded) {
            return [];
        }

        const
            { expandedFunctionIDs, teamFilter } = this.state,
            { companyFunctions } = this.props,

            teams = companyFunctions.Data.filter(({ FatherID }) => FatherID === fatherID);

        return teams
            .map(({ ID, Name }) => {
                const
                    checked = teamFilter.includes(ID),
                    hasChildren = companyFunctions.Data.some(({ FatherID }) => FatherID === ID);

                return [
                    <div key={`team-${ID}`} className={`button inline group depth-${depth}`}>
                        <button
                            className={`inline fa fa-${expandedFunctionIDs.includes(ID) ? 'minus' : 'plus'}-square-o`}
                            disabled={!hasChildren}
                            onClick={this._onToggleFunctionsFilterParentFunctionClick.bind(this, ID)}
                        />
                        <button className={`inline${checked ? ' checked' : ''}`} onClick={this._onToggleTeamFilterClick.bind(this, ID)}>
                            <span className="formatted-resource">{Name}</span>
                            {checked ? <span className="fa fa-check" /> : null}
                        </button>
                    </div>,
                    ...hasChildren && expandedFunctionIDs.includes(ID) ? this._renderTeamFilterOptions(ID, depth + 1) : []
                ];
            })
            .flatten();
    }

    private _renderVirtualTeamFilterOptions(): ReactNode[] {
        if (!this.state.virtualTeamsFilterGroupExpanded) {
            return [];
        }

        const
            teams = this.props.companyVirtualTeams.Data,
            { virtualTeamFilter } = this.state;

        return teams
            .map(({ ID, Name }, _, __, checked = virtualTeamFilter.includes(ID)) => [
                <button key={`virtual-team-${ID}`} className={`inline depth-2 ${checked ? ' checked' : ''}`} onClick={this._onToggleVirtualTeamFilterClick.bind(this, ID)}>
                    <span className="formatted-resource">{Name}</span>
                    {checked ? <span className="fa fa-check" /> : null}
                </button>
            ]);
    }

    private _renderPositionFilter() {
        const
            { positionFilter } = this.state,
            positions = this.props.users.Data
                .filter(({ ID, Title }) => Title && this.props.userCompanyAccesses.Data.some(({ UserID }) => UserID === ID))
                .map(({ Title }) => Title)
                .unique()
                .sortBy(value => value.toLowerCase());

        return (
            <DropPanel
                className={`user-selection-list-filter${!positionFilter.length ? ' default' : ''}`}
                title={positions.filter(position => positionFilter.includes(position)).join(', ') || { id: 'users.position' }}
                // eslint-disable-next-line react/jsx-handler-names
                icon={positionFilter.length ? <button className="inline fa fa-times" onClick={this._onClearFilterClick.bind(this, TableColumn.POSITION)} /> : DropPanel.AUTO_ICON}
                disabled={!positions.length}
            >
                {positions.map((position, _, __, checked = positionFilter.includes(position)) =>
                    <button key={position} className={`inline${checked ? ' checked' : ''}`} onClick={this._onTogglePositionFilterClick.bind(this, position)}>
                        <span className="formatted-resource">{position}</span>
                        {checked ? <span className="fa fa-check" /> : null}
                    </button>
                )}
            </DropPanel>
        );
    }

    render() {
        const
            { disabled, session } = this.props,
            { userFilter, emailFilter, roleFilter, teamFilter, virtualTeamFilter, positionFilter, editedUser } = this.state,

            isAdmin = session.IsUser4SAdministrator || (
                session.UseUserRoleBasedBilling ?
                    session.UserRoleBillingUserTypeID === UserRoleBillingUserType.FACILITATOR :
                    session.IsUserAdministrator
            ),
            isManager = session.UseUserRoleBasedBilling && session.UserRoleBillingUserTypeID === UserRoleBillingUserType.MANAGER,

            columns = this.props.columns!.filter(column => column !== TableColumn.POSITION || session.ProductPackageID !== ProductPackage.SMART_STRATEGY),
            value = this.props.value!,

            columnWidth = {
                width: columns.includes(TableColumn.SELECTION) ?
                    `calc((100% - ${SELECTION_CELL_WIDTH}px) / ${Math.max(1, columns.length - 1)})` :
                    `${100 / Math.max(1, columns.length)}%`
            },

            users = this.state.users.filter(({ user, access, roles, virtualTeamID }) =>
                (!userFilter.length || `${user.FirstName} ${user.LastName}`.toLowerCase().includes(userFilter.toLowerCase())) &&
                (!emailFilter.length || user.Email.toLowerCase().includes(emailFilter.toLowerCase())) &&
                (!roleFilter.length || roles.some(({ id }) => roleFilter.includes(id)) && (!access.IsExternalUser || roleFilter.includes(EXTERNAL_USER))) &&
                (
                    !teamFilter.length && !virtualTeamFilter.length ||
                    teamFilter.includes(user.CompanyFunctionID) || virtualTeamID && virtualTeamFilter.includes(virtualTeamID)
                ) &&
                (!positionFilter.length || positionFilter.includes(user.Title))
            );

        return (
            <div className="user-selection-list">
                {!this.props.fixedHeader ? this._renderHeader(columns, users) : null}

                <div ref="users" className="users">
                    {users
                        .map(({ user, roles, team, virtualTeamName, loginDate }) => {
                            let userName = `${user.FirstName} ${user.LastName}`;
                            if (userFilter) { userName = userName.replace(new RegExp(`(${userFilter})`, 'gi'), '**$1**').replaceAll('****', ''); }

                            let email = user.Email;
                            if (emailFilter) {
                                email = email.replace(new RegExp(`(${emailFilter})`, 'gi'), '**$1**').replaceAll('****', '');
                                email = email.replace(/(.*?)(\*\*.*\*\*)(.*)/g, '`$1`$2`$3`').replaceAll('``', '');
                            }

                            return (
                                <div key={user.ID} className="user-row">
                                    {
                                        columns.includes(TableColumn.SELECTION) ?
                                            <div className="column selection">
                                                <Checkbox value={value.includes(user.ID)} disabled={disabled} onChange={this._onUserSelectionChange.bind(this, user.ID)} />
                                            </div> :
                                            null
                                    }
                                    {
                                        columns.includes(TableColumn.USER) ?
                                            <div className="column user-name" style={columnWidth}>
                                                <button
                                                    className="inline link"
                                                    disabled={!isAdmin && (!isManager || user.ID !== session.UserID && user.DirectManagerID !== session.UserID)}
                                                    onClick={this._onEditUserClick.bind(this, user.ID)}
                                                >
                                                    {!userFilter ? userName : <MarkdownBlock value={userName} inline />}
                                                </button>
                                            </div> :
                                            null
                                    }
                                    {
                                        columns.includes(TableColumn.EMAIL) ?
                                            <div className="column email" style={columnWidth}>{!emailFilter ? email : <MarkdownBlock value={email} inline />}</div> : null
                                    }
                                    {
                                        columns.includes(TableColumn.ROLE) ?
                                            <div className="column" style={columnWidth}>
                                                {roles
                                                    .filter(({ id, name }) => name && (session.UseUserRoleBasedBilling || id !== SystemUserType.NORMAL_USER))
                                                    .map(({ name }) => name)
                                                    .join(', ')}
                                            </div> :
                                            null
                                    }
                                    {
                                        columns.includes(TableColumn.TEAM) ?
                                            <div className="column" style={columnWidth}>
                                                {[team, virtualTeamName].filter(name => !!name).join(', ')}
                                            </div> : null
                                    }
                                    {columns.includes(TableColumn.POSITION) ? <div className="column" style={columnWidth}>{user.Title}</div> : null}
                                    {
                                        columns.includes(TableColumn.LAST_LOGIN) ?
                                            <div className="column" style={columnWidth}>{loginDate ? <TimeStamp value={loginDate} /> : null}</div> :
                                            null
                                    }
                                </div>
                            );
                        })
                        .or(<div className="empty"><FormattedResource resource={{ id: 'users.no_results' }} /></div>)}
                </div>

                {
                    this.props.showUserCount ?
                        <FormattedResource
                            className="user-count"
                            resource={{ id: 'users.user_selection_list_count' }}
                            values={{
                                total: this.state.userCount,
                                visible: users.length,
                                hidden: this.state.userCount - users.length
                            }}
                        /> :
                        null
                }


                {this.props.fixedHeader ? this._renderHeader(columns, users) : null}

                {
                    editedUser ?
                        <Popup className="new edit-user" dimmed closeMode={Popup.CloseMode.CLOSE_BUTTON} onClose={this._onEditUserCancel}>
                            <ComponentLoader
                                scope="user-list"
                                component="UserManagement"
                                userID={editedUser.ID}
                                onCancel={this._onEditUserCancel}
                                onUserSaved={this._onEditUserCancel}
                            />
                        </Popup> :
                        null
                }
            </div>
        );
    }
}

export default injectIntl(connect<StoreProps, OwnProps>(state => ({
    users: (state.app as any).users,
    userCompanyAccesses: (state.app as any).userCompanyAccesses,
    userReport: (state.userList as any).userReport,
    systemUserTypes: state.app.systemUserTypes,
    userRoleBillingUserTypes: state.app.userRoleBillingUserTypes,
    companyFunctions: state.corporateStructure.companyFunctions,
    companyVirtualTeams: state.corporateStructure.companyVirtualTeams,
    companyVirtualTeamMembers: state.corporateStructure.companyVirtualTeamMembers
}))(UserSelectionList));
